/**
 * outboundUrlMacros — WEB-2262: подстановка меток исходящего адреса НА КЛИЕНТЕ.
 *
 * ЗАЧЕМ ВТОРОЙ ИСПОЛНИТЕЛЬ ОДНОГО ПРАВИЛА. Все остальные уходы человека на
 * чужую страницу (правило «увести по адресу», блок «редирект», направление
 * смартлинка) идут через сервер, и метки подставляет ОДНА серверная функция
 * `backend/src/shared/utils/outbound-url-macros.ts`. Кнопка-ссылка через
 * серверную точку ухода не идёт намеренно (WEB-2164 п.6: это элемент экрана, а
 * не концовка квиза, и шаг «ушёл на оплату» по ней не пишется), поэтому
 * подставить значения в её адрес может только браузер. Решение владельца
 * 18.09 по WEB-2262 — вариант «а»: подставлять, а не оставлять автора с
 * литеральными скобками в адресе чужого пейволла.
 *
 * ПОЧЕМУ ЭТО НЕ «ЕЩЁ ОДНА КОПИЯ, КОТОРАЯ РАЗЪЕДЕТСЯ». Две копии правила
 * расходятся молча — это шрам WEB-1687. Поэтому у правила два конца и
 * двусторонний сторож:
 *   · таблица случаев ОДНА и лежит у сервера
 *     (`backend/src/shared/utils/outbound-url-macros.cases.json`);
 *   · её читает spec сервера (`outbound-url-macros.spec.ts`) И тест кабинета
 *     (`__tests__/outboundUrlMacros.web2262.test.ts`) — с диска, не копией;
 *   · сторож `backend/src/shared/guards/outbound-macros-two-players-ends.web2262.spec.ts`
 *     сверяет МНОЖЕСТВА имён в обе стороны и то, что оба конца читают ту же
 *     таблицу. Подменишь одно ожидание в таблице — красны оба конца сразу.
 *
 * ПОВЕДЕНИЕ ПОВТОРЕНО БУКВА В БУКВУ (см. серверный файл, там же и обоснования):
 *   · метка — ОДИНАРНЫЕ фигурные скобки; адрес с ратифицированной запрещённой
 *     формой `{{…}}` не трогается вовсе;
 *   · подстановка только в ЗНАЧЕНИЯ параметров запроса: схема, хост, путь,
 *     якорь и имена параметров не меняются ни при каком значении;
 *   · каждое значение проходит `encodeURIComponent`;
 *   · белый список закрытый: пять меток кампании, метка клика, посетитель и
 *     `answer.<идентификатор вопроса>`; всё остальное (включая личное — почту,
 *     телефон, имя) значения не получает и даёт пустоту;
 *   · значение схлопнулось в пустоту целиком — параметр опускается, а не едет
 *     пустым;
 *   · только абсолютный http(s)-адрес разбирается на части.
 *
 * ГЕЙТ СОГЛАСИЯ ЗДЕСЬ НЕ ЖИВЁТ. Значения приходят снаружи: их собирает плеер
 * (`outboundMacroValues()` в QuizScreenWizard) под тем же гейтом согласия и
 * сигнала GPC, что и остальные уходы. Отказал — значений нет вовсе, и метки
 * выбрасываются пустыми. Свой гейт здесь был бы третьей копией правила.
 */

/** Пять меток кампании + метка клика + посетитель. Закрытый список. */
export const OUTBOUND_MACRO_NAMES: ReadonlySet<string> = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'click_id',
  'visitor_id',
]);

/** Форма метки ответа: `{answer.<идентификатор вопроса>}`. */
export const ANSWER_MACRO_PREFIX = 'answer.';

/**
 * Имя метки: буквы, цифры, подчёркивание, точка и дефис (точка — только для
 * `answer.<id>`; дефис — потому что идентификаторы вопросов приходят с ним:
 * `answer.q-goal`, UUID). Скобки одинарные — форма постбек-макросов.
 *
 * Та же форма и та же проза — у сервера (`backend/src/shared/utils/
 * outbound-url-macros.ts`): регулярки совпадали, а объяснения разошлись
 * (фикс-пачка 45, FX2).
 */
const MACRO_RE = /\{([A-Za-z0-9_][A-Za-z0-9_.-]*)\}/g;

/** Абсолютный веб-адрес — только такой разбирается на части. */
const ABSOLUTE_HTTP_RE = /^https?:\/\//i;

export interface OutboundMacroValues {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  click_id?: string;
  /** БРАУЗЕРНЫЙ идентификатор посетителя (`qb_visitor_id`). */
  visitor_id?: string;
  /** Пары «идентификатор вопроса → идентификатор варианта». */
  answers?: Record<string, string>;
}

/** Значение метки по имени. Неизвестное имя — пустая строка, не падение. */
function lookup(name: string, values: OutboundMacroValues): string {
  if (name.startsWith(ANSWER_MACRO_PREFIX)) {
    const questionId = name.slice(ANSWER_MACRO_PREFIX.length);
    const value = values.answers?.[questionId];
    return typeof value === 'string' ? value : '';
  }
  if (!OUTBOUND_MACRO_NAMES.has(name)) return '';
  const value = (values as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : '';
}

/**
 * Подставляет метки в строку параметров адреса. Зеркало серверной
 * `expandOutboundUrlMacros`; разбор строкой, а не через `URL`, — по той же
 * причине: `URL` пересобирает адрес и перекодирует то, чего мы не трогали, а
 * автор вправе ожидать, что всё, кроме его меток, доедет байт в байт.
 */
export function expandOutboundUrlMacros(
  template: string,
  values: OutboundMacroValues,
): string {
  if (typeof template !== 'string' || template.length === 0) return '';
  // Ратифицированная запрещённая форма `{{…}}` — не наша; такой адрес не
  // трогаем вовсе, иначе одинарная скобка внутри двойной дала бы подстановку
  // внутрь того самого синтаксиса, ради запрета которого правило и вводили.
  if (template.includes('{{') || template.includes('}}')) return template;
  if (!template.includes('{')) return template;
  if (!ABSOLUTE_HTTP_RE.test(template)) return template;

  const hashAt = template.indexOf('#');
  const hash = hashAt === -1 ? '' : template.slice(hashAt);
  const withoutHash = hashAt === -1 ? template : template.slice(0, hashAt);

  const queryAt = withoutHash.indexOf('?');
  if (queryAt === -1) return template;

  const head = withoutHash.slice(0, queryAt);
  const query = withoutHash.slice(queryAt + 1);
  if (query.length === 0) return template;

  const kept: string[] = [];
  for (const pair of query.split('&')) {
    const eq = pair.indexOf('=');
    // Имя параметра не подставляется никогда.
    if (eq === -1) {
      kept.push(pair);
      continue;
    }
    const key = pair.slice(0, eq);
    const rawValue = pair.slice(eq + 1);
    if (!rawValue.includes('{')) {
      kept.push(pair);
      continue;
    }
    const substituted = rawValue.replace(
      new RegExp(MACRO_RE.source, 'g'),
      (_full: string, name: string) => encodeURIComponent(lookup(name, values)),
    );
    // Значение схлопнулось в пустоту — параметр опускается, а не едет пустым.
    if (substituted.length === 0) continue;
    kept.push(`${key}=${substituted}`);
  }

  const rebuiltQuery = kept.join('&');
  return rebuiltQuery.length === 0
    ? `${head}${hash}`
    : `${head}?${rebuiltQuery}${hash}`;
}

/** Есть ли в адресе хоть одна метка нашей формы (одинарные скобки). */
export function hasOutboundMacros(raw: string | undefined | null): boolean {
  if (typeof raw !== 'string' || raw.length === 0) return false;
  if (raw.includes('{{') || raw.includes('}}')) return false;
  return new RegExp(MACRO_RE.source).test(raw);
}

/**
 * Значения плеера (имена полей хранилища) → имена МЕТОК.
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ ФУНКЦИЯ. Плеер держит значения в своих именах
 * (`utmSource`, `clickId`, `macroVisitorId` — см. `quizExitUrl.ts`), а метка
 * называется так, как её пишет автор в адресе. Сопоставление живёт в ОДНОМ
 * месте: разложи его по вызовам — однажды перепутают две строки, и метка
 * молча поедет пустой (класс шрама WEB-1687).
 */
export function toOutboundMacroValues(params: {
  utm?: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  };
  clickId?: string | null;
  macroVisitorId?: string | null;
  answers?: Record<string, string>;
}): OutboundMacroValues {
  const nonEmpty = (value: string | null | undefined): string | undefined =>
    typeof value === 'string' && value.trim() !== '' ? value : undefined;
  const utm = params.utm ?? {};
  return {
    utm_source: nonEmpty(utm.utmSource),
    utm_medium: nonEmpty(utm.utmMedium),
    utm_campaign: nonEmpty(utm.utmCampaign),
    utm_content: nonEmpty(utm.utmContent),
    utm_term: nonEmpty(utm.utmTerm),
    click_id: nonEmpty(params.clickId),
    visitor_id: nonEmpty(params.macroVisitorId),
    answers: params.answers ?? {},
  };
}
